import { useFusionAuth } from "@fusionauth/react-sdk";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { Box, TextField, Typography } from "@mui/material";
import React, { type ChangeEvent, useEffect, useState } from "react";
import DateRange from "../../../components/DateRange.tsx";

export default function Protocol({
  displayContent,
  displayErrors,
  onUpdateData,
  projectData,
  companySettingMinDate,
}: {
  displayContent: boolean;
  displayErrors: boolean;
  onUpdateData: (data: Partial<ProjectContextType>) => void;
  projectData: ProjectContextType;
  companySettingMinDate: Date;
}) {
  const [preIndexValidation, setPreIndexValidation] = useState(
    displayErrors && projectData.defaultPreIndexDays === undefined,
  );
  const [postIndexValidation, setPostIndexValidation] = useState(
    displayErrors && projectData.defaultPostIndexDays === undefined,
  );

  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");

  function handleStartDate(date: Date | undefined) {
    onUpdateData({ startDate: date });
  }

  function handleEndDate(date: Date | undefined) {
    onUpdateData({ endDate: date });
  }

  function handleChangeDetails(event: ChangeEvent<HTMLTextAreaElement>) {
    if (event.target.value !== null && event.target.value.length <= 500) {
      onUpdateData({ additionalCriteria: event.target.value });
    }
  }

  function handleChangeNumPreIndexProcedure(
    event: ChangeEvent<HTMLInputElement>,
  ) {
    if (event.target.value === "" || event.target.value === null) {
      onUpdateData({ defaultPreIndexDays: undefined });
      setPreIndexValidation(true);
      return;
    }
    const numberParse = parseInt(event.target.value);
    if (isNaN(numberParse) || numberParse < 0) {
      setPreIndexValidation(true);
      return;
    }
    onUpdateData({ defaultPreIndexDays: numberParse });
    setPreIndexValidation(false);
  }

  function handleChangeNumPostIndexProcedure(
    event: ChangeEvent<HTMLInputElement>,
  ) {
    if (event.target.value === "" || event.target.value === null) {
      onUpdateData({ defaultPostIndexDays: undefined });
      setPostIndexValidation(true);
      return;
    }
    const numberParse = parseInt(event.target.value);
    if (isNaN(numberParse) || numberParse < 0) {
      setPostIndexValidation(true);
      return;
    }
    onUpdateData({ defaultPostIndexDays: numberParse });
    setPostIndexValidation(false);
  }

  useEffect(() => {
    if (projectData.startDate === undefined) {
      onUpdateData({ startDate: companySettingMinDate });
    }
    if (projectData.endDate === undefined) {
      onUpdateData({ endDate: new Date() });
    }
  }, [
    projectData.startDate,
    projectData.endDate,
    companySettingMinDate,
    onUpdateData,
  ]);

  if (!displayContent) {
    return null;
  }

  return (
    <Box className="protocol-section" sx={{ bgcolor: "grey.50" }}>
      <Typography variant="body2">
        Enter some details for your project and we’ll help you stay on track to
        meet the requirements. If you need to exclude specific patient
        populations you can do so once the project is ready for use.
      </Typography>
      <Typography color="text.secondary">* Required fields</Typography>
      <section className="subsection">
        <Typography variant="body1">Procedure Date Range</Typography>
        <DateRange
          displayErrors={displayErrors}
          startDate={projectData.startDate || companySettingMinDate}
          endDate={projectData.endDate || new Date()}
          onStartDateChange={handleStartDate}
          onEndDateChange={handleEndDate}
          disabled={!isAdmin}
          minDate={companySettingMinDate}
        />
      </section>
      <section className="subsection">
        <Typography variant="body1">Visit Date Range</Typography>
        <div className="date-range">
          <TextField
            label="Days Pre-Index Procedure"
            type="number"
            value={projectData.defaultPreIndexDays}
            error={preIndexValidation}
            onChange={handleChangeNumPreIndexProcedure}
            disabled={!isAdmin}
            slotProps={{
              htmlInput: {
                "data-testid": "Protocol-pre-index-procedure",
              },
            }}
            required
            helperText={"Please enter a value greater than or equal to 0"}
            sx={{ width: "233px" }}
          />
          <div className="visit-range-section-chevron">
            <ChevronRightOutlinedIcon className="icon" />
          </div>
          <TextField
            label="Days Post-Index Procedure"
            type="number"
            value={projectData.defaultPostIndexDays}
            slotProps={{
              htmlInput: {
                min: 0,
                step: 1,
                "data-testid": "Protocol-post-index-procedure",
              },
            }}
            error={postIndexValidation}
            onChange={handleChangeNumPostIndexProcedure}
            disabled={!isAdmin}
            required
            helperText={"Please enter a value greater than or equal to 0"}
            sx={{ width: "233px" }}
          />
        </div>
      </section>

      <section className="subsection">
        <Typography variant="body1">
          List any specific inclusion or exclusion criteria
        </Typography>
        <TextField
          label="Details"
          value={projectData.additionalCriteria || ""}
          onChange={handleChangeDetails}
          minRows={6}
          multiline
          fullWidth
          helperText={
            projectData.additionalCriteria
              ? `${projectData.additionalCriteria.length} of 500 characters`
              : "0 of 500 characters"
          }
        />
      </section>
    </Box>
  );
}
